import { Prop, Vue, Watch } from 'vue-property-decorator';
import { WatchOptions } from 'vue';
import { ThingDTO } from '@/models/ThingDTO';

export default abstract class AutoComplete<T> extends Vue {
  @Prop() public value?: T;
  @Prop({ required: true }) public name?: string;
  @Prop() public label?: string;
  @Prop() public placeholder?: string;
  @Prop({ default: false }) public required?: boolean;
  @Prop({ default: false }) public disabled?: boolean;
  @Prop({ default: false }) public readonly?: boolean;
  public items: ThingDTO[] = [];
  public isOpen: boolean = false;
  public results: ThingDTO[] = [];
  public search: ThingDTO = {} as ThingDTO;
  public isLoading: boolean = false;
  public arrowCounter: number = 0;
  public activedescendant: string = '';
  protected Value: T|undefined;

  public abstract convertValue(): T;
  public abstract handleClickOutside(evt: any): void;

  protected onChange(value: string) {
    this.search.name = value;
    this.search.id = -99;
    this.filterResults();
    this.isOpen = true;
  }

  protected filterResults(): void {
    this.results = this.items.filter((item) => item.name.toLowerCase().indexOf(this.search.name.toLowerCase()) > -1);
  }

  /**
   * Set Value to the selected element from the autocomplete
   */
  protected setResult(result: any): void {
    this.search = result;
    this.isOpen = false;
    this.Value = this.convertValue();
    this.$emit('input', this.Value);
  }

  protected onArrowDown(evt: any): void {
    if (this.isOpen) {
      if (this.arrowCounter < this.results.length) {
        this.arrowCounter = this.arrowCounter + 1;
        this.setActiveDescendant();
      }
    }
  }

  protected onArrowUp(): void {
    if (this.isOpen) {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter - 1;
        this.setActiveDescendant();
      }
    }
  }

  protected onEnter(): void {
    if (this.results.length > 0 && this.arrowCounter >= 0) {
      this.search = this.results[this.arrowCounter];
      this.arrowCounter = -1;
      this.isOpen = false;
      this.Value = this.convertValue();
      this.$emit('input', this.Value);
    } else {
      this.search = {} as ThingDTO;
      this.arrowCounter = -1;
      this.isOpen = false;
      this.Value = undefined;
      this.$emit('input', this.Value);
    }
  }

  protected setActiveDescendant(): void {
    this.activedescendant = this.getId(this.arrowCounter);
  }

  protected getId(index: number): string {
    return `result-item-${index}`;
  }

  protected isSelected(i: number): boolean {
    return i === this.arrowCounter;
  }

  @Watch('items', { deep: true } as WatchOptions)
  protected onItemsChanged(value: [], oldValue: []) {
    // actually compare them
    if (!oldValue || value && value.length !== oldValue.length) {
      this.results = value;
      this.isLoading = false;
    }
  }

  protected mounted() {
    document.addEventListener('click', this.handleClickOutside);
  }

  protected destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
